import { useState, useEffect } from "react";
import api from "@/lib/api";
import SectionTitle from "@/components/SectionTitle";
import { Rss, ExternalLink, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

const sentClass = (s) => {
  if (s === "Positif") return "bg-[var(--bull)]/15 text-bull border-[var(--bull)]/25";
  if (s === "Negatif") return "bg-[var(--bear)]/15 text-bear border-[var(--bear)]/25";
  return "bg-[var(--amber)]/15 text-amber border-[var(--amber)]/25";
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))} mnt lalu`;
  if (diff < 1440) return `${Math.floor(diff / 60)} jam lalu`;
  return `${Math.floor(diff / 1440)} hari lalu`;
};

export default function NewsSection() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = (refresh = false) => {
    if (refresh) setRefreshing(true);
    api.get(`/news${refresh ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setRefreshing(false));
  };

  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const filtered = active === "all" ? items : items.filter((i) => i.source_id === active);
  const visible = showAll ? filtered : filtered.slice(0, 3);

  return (
    <section id="news" className="scroll-mt-20">
      <SectionTitle
        eyebrow="Live Aggregator · 6 Portal"
        title="Portal Berita Saham Terintegrasi"
        desc="Berita terkini langsung dari Pasardana, IDX Channel, Bloomberg, Bisnis.com, Emitennews, dan IDN Financials."
        icon={Rss}
      />
      <div className="card p-4 lg:p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setActive("all"); setShowAll(false); }} data-testid="news-filter-all" className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active === "all" ? "bg-[var(--accent)] text-[#07090E] border-[var(--accent)]" : "bg-[var(--surface-base)] text-[var(--text-secondary)] border-[var(--border)] hover:bg-[var(--surface-hover)]"}`}>Semua</button>
            {(data?.sources || []).map((s) => (
              <button key={s.id} onClick={() => { setActive(s.id); setShowAll(false); }} data-testid={`news-filter-${s.id}`} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active === s.id ? "bg-[var(--accent)] text-[#07090E] border-[var(--accent)]" : "bg-[var(--surface-base)] text-[var(--text-secondary)] border-[var(--border)] hover:bg-[var(--surface-hover)]"}`}>{s.label}</button>
            ))}
          </div>
          <button onClick={() => load(true)} disabled={refreshing} data-testid="news-refresh-btn" className="p-2 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)] hover:text-accent transition-colors">
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>

        {!data ? (
          <div className="p-8 text-center text-[var(--text-secondary)] text-sm">Mengambil berita terkini...</div>
        ) : (
          <div className="space-y-2">
            {visible.map((n) => (
              <motion.a
                key={n.id}
                href={n.link}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 3 }}
                transition={{ duration: 0.15 }}
                data-testid="news-item"
                className="block bg-[var(--surface-base)] rounded-lg p-3.5 border border-[var(--border-subtle)] hover:border-[var(--accent)]/30 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-[10px] font-semibold text-accent">{n.source}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${sentClass(n.sentiment)}`}>{n.sentiment}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[var(--text-muted)]">{n.category}</span>
                      <span className="text-[10px] text-[var(--text-muted)] font-mono-data">{timeAgo(n.published)}</span>
                    </div>
                    <p className="text-sm text-[var(--text-primary)] leading-snug group-hover:text-accent transition-colors">{n.title}</p>
                  </div>
                  <ExternalLink size={14} className="text-[var(--text-muted)] group-hover:text-accent shrink-0 mt-1" />
                </div>
              </motion.a>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-[var(--text-muted)] text-sm">Tidak ada berita untuk sumber ini saat ini.</div>}
            {filtered.length > 3 && (
              <button onClick={() => setShowAll((v) => !v)} data-testid="news-more" className="w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] text-sm font-semibold text-accent hover:bg-[var(--surface-hover)] transition-colors">
                {showAll ? "Tampilkan lebih sedikit" : `Selengkapnya (${filtered.length - 3} berita lainnya)`}
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
